import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, already_verified, error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Lien de vérification invalide');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/verify-email?token=${token}`);
      if (response.data.already_verified) {
        setStatus('already_verified');
        setMessage('Votre email a déjà été vérifié');
      } else {
        setStatus('success');
        setMessage('Votre email a été vérifié avec succès !');
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Lien de vérification invalide ou expiré');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center px-4" data-testid="verify-email-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'loading' && (
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            )}
            {status === 'already_verified' && (
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-blue-600" />
              </div>
            )}
            {status === 'error' && (
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl">
            {status === 'loading' && 'Vérification en cours...'}
            {status === 'success' && 'Email vérifié !'}
            {status === 'already_verified' && 'Déjà vérifié'}
            {status === 'error' && 'Erreur de vérification'}
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(status === 'success' || status === 'already_verified') && (
            <div className="space-y-3">
              <p className="text-center text-slate-600 dark:text-slate-400">
                Vous pouvez maintenant accéder à toutes les fonctionnalités de drbooking.
              </p>
              <Button 
                className="w-full" 
                onClick={() => navigate('/login')}
                data-testid="goto-login-btn"
              >
                Se connecter
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
          {status === 'error' && (
            <div className="space-y-3">
              <p className="text-center text-slate-600 dark:text-slate-400">
                Le lien peut avoir expiré ou être invalide. Essayez de vous reconnecter pour recevoir un nouveau lien.
              </p>
              <Button 
                className="w-full" 
                onClick={() => navigate('/login')}
                data-testid="goto-login-btn"
              >
                Retour à la connexion
              </Button>
            </div>
          )}
          <div className="text-center pt-4 border-t">
            <Link to="/" className="text-sm text-slate-500 hover:text-primary">
              Retour à l'accueil
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
